import React from 'react';
import { Link } from 'react-router-dom';
import { DocsLayout } from '../components/DocsLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ZoomIn, Navigation, Info, CheckCircle, AlertTriangle } from 'lucide-react';

export const RecallMapGuidePage: React.FC = () => {
  return (
    <DocsLayout
      title='Recall Map'
      description='Interactive geographic visualization of vehicle recalls across your dealer network'
      breadcrumbs={[
        { label: 'Documentation', path: '/docs' },
        { label: 'Features', path: '/docs/features' },
        { label: 'Recall Map' }
      ]}
    >
      <div className='max-w-[1000px]'>
        {/* Overview Section */}
        <section className='mb-20'>
          <div className='bg-blue-50 dark:bg-blue-950/30 border-l-4 border-blue-600 p-6 rounded-r-xl mb-10'>
            <div className='flex gap-4'>
              <Info className='h-6 w-6 text-blue-600 flex-shrink-0 mt-1' />
              <div>
                <h3 className='text-lg font-semibold text-blue-900 dark:text-blue-100 mb-3'>
                  What is the Recall Map?
                </h3>
                <p className='text-sm leading-6 text-blue-800 dark:text-blue-200'>
                  The Recall Map is your geographic visualization tool for finding and selecting
                  recalls. It provides an interactive map view of all vehicle recalls across your
                  dealer network, helping you visualize recall distribution, plan mobile routes, and
                  quickly identify clusters of work.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Accessing the Recall Map Section */}
        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Accessing the Recall Map</h2>
          <p className='text-sm text-muted-foreground mb-10 leading-6'>
            Navigate to the Recall Map from the sidebar:
          </p>
          <p className='text-sm leading-6'>
            <strong>Sidebar → SEARCH → Recall Map</strong>
          </p>
        </section>

        {/* Understanding the Interface Section */}
        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Understanding the Interface</h2>
          <p className='text-sm text-muted-foreground mb-10 leading-6'>
            The Recall Map interface is divided into three main sections:
          </p>
          <ul className='space-y-2 ml-6'>
            <li className='text-sm leading-6'>
              <strong>Left Side (70% width):</strong> Interactive map displaying recall locations
            </li>
            <li className='text-sm leading-6'>
              <strong>Right Side (30% width):</strong> Scrollable dealer information panel
            </li>
            <li className='text-sm leading-6'>
              <strong>Top:</strong> Collapsible filter accordion sections
            </li>
          </ul>
        </section>

        {/* Map Elements Explained Section */}
        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Map Elements Explained</h2>

          <div className='space-y-12'>
            {/* Cluster Bubbles */}
            <div>
              <h3 className='text-lg font-semibold mb-4'>1. Cluster Bubbles (When Zoomed Out)</h3>
              <p className='text-sm text-muted-foreground mb-6 leading-6'>
                When viewing a large geographic area, recalls are grouped into cluster bubbles:
              </p>

              <Card className='rounded-xl border shadow-sm'>
                <CardHeader>
                  <CardTitle className='text-lg font-semibold'>Cluster Bubble Details</CardTitle>
                </CardHeader>
                <CardContent className='space-y-4'>
                  <div>
                    <h4 className='font-semibold mb-2'>What they look like:</h4>
                    <ul className='space-y-1 text-sm leading-6 ml-4'>
                      <li>Circular bubbles with numbers</li>
                      <li>Grayscale colors: Light gray → Dark gray → Black</li>
                      <li>Larger bubbles indicate more recalls</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className='font-semibold mb-2'>What the number means:</h4>
                    <p className='text-sm leading-6 text-muted-foreground'>
                      The total number of recalls in that cluster. For example, "45" means 45
                      vehicles across multiple dealers in that area.
                    </p>
                  </div>
                  <div>
                    <h4 className='font-semibold mb-2'>Color meanings:</h4>
                    <div className='space-y-1 text-sm'>
                      <div className='flex items-center gap-2'>
                        <Badge variant='outline'>Light gray</Badge>
                        <span>1-10 vehicles</span>
                      </div>
                      <div className='flex items-center gap-2'>
                        <Badge variant='outline'>Medium gray</Badge>
                        <span>11-50 vehicles</span>
                      </div>
                      <div className='flex items-center gap-2'>
                        <Badge variant='outline'>Dark gray</Badge>
                        <span>51-150 vehicles</span>
                      </div>
                      <div className='flex items-center gap-2'>
                        <Badge variant='outline'>Black</Badge>
                        <span>150+ vehicles</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className='font-semibold mb-2'>How to use:</h4>
                    <ul className='space-y-1 text-sm leading-6 ml-4'>
                      <li>Click cluster to zoom into that area</li>
                      <li>Clusters break apart as you zoom in</li>
                      <li>Hover to see quick count</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Individual Dealer Markers */}
            <div>
              <h3 className='text-lg font-semibold mb-4'>
                2. Individual Dealer Markers (When Zoomed In)
              </h3>
              <p className='text-sm text-muted-foreground mb-6 leading-6'>
                When zoomed in closer, you'll see individual dealer markers:
              </p>

              <Card className='rounded-xl border shadow-sm'>
                <CardHeader>
                  <CardTitle className='text-lg font-semibold'>Dealer Marker Details</CardTitle>
                </CardHeader>
                <CardContent className='space-y-4'>
                  <div>
                    <h4 className='font-semibold mb-2'>What they look like:</h4>
                    <ul className='space-y-1 text-sm leading-6 ml-4'>
                      <li>Pin-style markers</li>
                      <li>Grayscale colors (same gradient as clusters)</li>
                      <li>Numbers on each marker</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className='font-semibold mb-2'>What the number means:</h4>
                    <p className='text-sm leading-6 text-muted-foreground'>
                      The number of vehicles with recalls at THAT specific dealer. For example, "3"
                      means 3 vehicles with recalls at this dealer location.
                    </p>
                  </div>
                  <div>
                    <h4 className='font-semibold mb-2'>Color meanings:</h4>
                    <div className='space-y-1 text-sm'>
                      <div className='flex items-center gap-2'>
                        <Badge variant='outline'>Light gray</Badge>
                        <span>1 vehicle</span>
                      </div>
                      <div className='flex items-center gap-2'>
                        <Badge variant='outline'>Medium gray</Badge>
                        <span>2-5 vehicles</span>
                      </div>
                      <div className='flex items-center gap-2'>
                        <Badge variant='outline'>Dark gray</Badge>
                        <span>6-15 vehicles</span>
                      </div>
                      <div className='flex items-center gap-2'>
                        <Badge variant='outline'>Black</Badge>
                        <span>16+ vehicles</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className='font-semibold mb-2'>How to use:</h4>
                    <ul className='space-y-1 text-sm leading-6 ml-4'>
                      <li>Hover to see dealer name and count</li>
                      <li>Click to see dealer popup with details</li>
                      <li>Click dealer card in right panel to expand full details</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Map Controls */}
            <div>
              <h3 className='text-lg font-semibold mb-4'>3. Map Controls</h3>

              <div className='grid gap-6 sm:grid-cols-2'>
                <Card className='rounded-xl border shadow-sm'>
                  <CardHeader className='pb-4'>
                    <div className='flex items-center gap-4 mb-2'>
                      <div className='p-3 bg-primary/10 rounded-xl'>
                        <ZoomIn className='h-8 w-8 text-primary' />
                      </div>
                      <CardTitle className='text-lg font-bold'>Zoom Controls</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className='space-y-2 text-sm leading-6 text-muted-foreground'>
                      <div>
                        <strong className='text-foreground'>Plus (+) button:</strong> Zoom in
                      </div>
                      <div>
                        <strong className='text-foreground'>Minus (-) button:</strong> Zoom out
                      </div>
                      <div>
                        <strong className='text-foreground'>Scroll wheel:</strong> Zoom in/out
                      </div>
                      <div>
                        <strong className='text-foreground'>Pinch gesture (mobile):</strong> Zoom
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className='rounded-xl border shadow-sm'>
                  <CardHeader className='pb-4'>
                    <div className='flex items-center gap-4 mb-2'>
                      <div className='p-3 bg-primary/10 rounded-xl'>
                        <Navigation className='h-8 w-8 text-primary' />
                      </div>
                      <CardTitle className='text-lg font-bold'>Pan & Navigation</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className='space-y-2 text-sm leading-6 text-muted-foreground'>
                      <div>
                        <strong className='text-foreground'>Click and drag:</strong> Pan the map
                      </div>
                      <div>
                        <strong className='text-foreground'>Swipe gesture (mobile):</strong> Pan the
                        map
                      </div>
                      <div>
                        <strong className='text-foreground'>Reset view:</strong> Click the Vehix
                        logo or home icon
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Using Filters Effectively Section */}
        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Using Filters Effectively</h2>
          <p className='text-sm text-muted-foreground mb-10 leading-6'>
            The Recall Map includes powerful filters organized into collapsible accordion sections.
            For detailed information about each filter, see the{' '}
            <Link to='/docs/features/filters' className='text-primary hover:underline'>
              Filters System guide
            </Link>
            .
          </p>

          <h3 className='text-lg font-semibold mb-4'>Quick Filter Summary</h3>
          <ul className='space-y-2 ml-6'>
            <li className='text-sm leading-6'>
              <strong>Vehicle Details:</strong> VIN, Make, Model, Year
            </li>
            <li className='text-sm leading-6'>
              <strong>Location:</strong> Dealer, City, ZIP Code, Radius
            </li>
            <li className='text-sm leading-6'>
              <strong>Recall Details:</strong> Campaign, Skill Level, Mobile Eligibility
            </li>
          </ul>
        </section>

        {/* Common Filter Workflows Section */}
        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-10'>Common Filter Workflows</h2>

          <div className='space-y-6'>
            <Card className='rounded-xl border shadow-sm'>
              <CardHeader>
                <CardTitle className='text-lg font-semibold'>
                  Workflow 1: Find All Recalls Near You
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ol className='space-y-2 text-sm leading-6 text-muted-foreground'>
                  <li>Expand "Location" section</li>
                  <li>Select your ZIP Code</li>
                  <li>Select Radius: 15 miles</li>
                  <li>Map updates automatically</li>
                  <li>See all recalls within 15 miles</li>
                </ol>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader>
                <CardTitle className='text-lg font-semibold'>
                  Workflow 2: Find Specific Campaign
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ol className='space-y-2 text-sm leading-6 text-muted-foreground'>
                  <li>Expand "Recall Details" section</li>
                  <li>Type campaign number in "Campaign" search</li>
                  <li>Select campaign from dropdown</li>
                  <li>Map shows only that campaign</li>
                  <li>See geographic distribution</li>
                </ol>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader>
                <CardTitle className='text-lg font-semibold'>
                  Workflow 3: Find Mobile-Eligible High Priority
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ol className='space-y-2 text-sm leading-6 text-muted-foreground'>
                  <li>Expand "Recall Details"</li>
                  <li>Skill Level: High</li>
                  <li>Mobile Eligible: Yes</li>
                  <li>Map shows high-priority mobile recalls</li>
                  <li>Perfect for planning mobile routes</li>
                </ol>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader>
                <CardTitle className='text-lg font-semibold'>Workflow 4: Search by VIN</CardTitle>
              </CardHeader>
              <CardContent>
                <ol className='space-y-2 text-sm leading-6 text-muted-foreground'>
                  <li>Expand "Vehicle Details"</li>
                  <li>Type VIN (partial works, e.g., last 6 digits)</li>
                  <li>Map zooms to that dealer</li>
                  <li>See that specific vehicle's recalls</li>
                </ol>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Active Filter Management Section */}
        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-10'>Active Filter Management</h2>

          <div className='space-y-10'>
            <div>
              <h3 className='text-lg font-semibold mb-4'>Filter Count Badges</h3>
              <p className='text-sm leading-6 text-muted-foreground'>
                Each section shows a count of active filters. For example, "Vehicle Details (2)"
                means 2 filters are active in that section. This helps you remember what's currently
                filtered.
              </p>
            </div>

            <div>
              <h3 className='text-lg font-semibold mb-4'>Clear Individual Filters</h3>
              <p className='text-sm leading-6 text-muted-foreground'>
                Click the X on any dropdown selection or clear the input field to remove that
                filter.
              </p>
            </div>

            <div>
              <h3 className='text-lg font-semibold mb-4'>Clear All Filters</h3>
              <p className='text-sm leading-6 text-muted-foreground'>
                Click the "Clear All" button at the top of the filter section to reset the entire
                map to show everything.
              </p>
            </div>
          </div>

          <div className='bg-green-50 dark:bg-green-950/30 border-l-4 border-green-600 p-6 rounded-r-xl mt-10'>
            <div className='flex gap-4'>
              <CheckCircle className='h-6 w-6 text-green-600 flex-shrink-0 mt-1' />
              <div>
                <h4 className='text-lg font-semibold text-green-900 dark:text-green-100 mb-2'>
                  Performance Benefit
                </h4>
                <p className='text-sm leading-6 text-green-800 dark:text-green-200'>
                  Filters apply instantly - no "Submit" button needed. Updates typically complete in
                  less than 500ms, providing a smooth, real-time experience.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Interacting with Dealers Section */}
        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-10'>Interacting with Dealers</h2>

          <div className='space-y-10'>
            <div>
              <h3 className='text-lg font-semibold mb-4'>In the Map</h3>
              <ol className='space-y-2 ml-6 text-sm leading-6 text-muted-foreground'>
                <li>Click dealer marker</li>
                <li>
                  Popup appears with:
                  <ul className='space-y-1 ml-6 mt-1'>
                    <li>Dealer name</li>
                    <li>Address</li>
                    <li>Total recalls at this dealer</li>
                    <li>"View Details" link</li>
                  </ul>
                </li>
              </ol>
            </div>

            <div>
              <h3 className='text-lg font-semibold mb-4'>In the Right Panel</h3>
              <ol className='space-y-2 ml-6 text-sm leading-6 text-muted-foreground'>
                <li>Scroll through dealer list</li>
                <li>
                  Each dealer card shows:
                  <ul className='space-y-1 ml-6 mt-1'>
                    <li>Dealer name</li>
                    <li>City, State</li>
                    <li>Vehicle count</li>
                    <li>Expand/collapse arrow</li>
                  </ul>
                </li>
                <li>Click card to expand</li>
                <li>
                  Expanded view shows:
                  <ul className='space-y-1 ml-6 mt-1'>
                    <li>Full address and phone</li>
                    <li>List of all recalls at this dealer</li>
                    <li>Select checkboxes to add to route</li>
                  </ul>
                </li>
              </ol>
            </div>
          </div>
        </section>

        {/* Selecting Recalls for Routing Section */}
        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Selecting Recalls for Routing</h2>
          <p className='text-sm text-muted-foreground mb-10 leading-6'>
            The Recall Map enables you to select specific recalls for assignment:
          </p>

          <Card className='rounded-xl border shadow-sm'>
            <CardHeader>
              <CardTitle className='text-lg font-semibold'>4-Step Selection Process</CardTitle>
            </CardHeader>
            <CardContent>
              <ol className='space-y-3 text-sm leading-6'>
                <li>
                  <strong>Step 1: Find Recalls</strong>
                  <div className='text-muted-foreground mt-1'>
                    Apply filters to narrow down and review dealer markers on map
                  </div>
                </li>
                <li>
                  <strong>Step 2: View Dealer Details</strong>
                  <div className='text-muted-foreground mt-1'>
                    Click dealer marker or expand dealer card to see all recalls at that dealer
                  </div>
                </li>
                <li>
                  <strong>Step 3: Select Specific Recalls</strong>
                  <div className='text-muted-foreground mt-1'>
                    Check the checkbox next to each recall you want, or check "Select All" for the
                    dealer
                  </div>
                </li>
                <li>
                  <strong>Step 4: Add to Route</strong>
                  <div className='text-muted-foreground mt-1'>
                    Click "Add to Route" button. Selected recalls are saved to your routing list.
                    Navigate to Manual Routing to assign to a technician.
                  </div>
                </li>
              </ol>
            </CardContent>
          </Card>
        </section>

        {/* Performance Tips Section */}
        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-10'>Performance Tips</h2>

          <div className='grid gap-6 sm:grid-cols-2'>
            <Card className='rounded-xl border shadow-sm'>
              <CardHeader>
                <CardTitle className='text-lg font-semibold'>For Faster Loading</CardTitle>
              </CardHeader>
              <CardContent>
                <div className='space-y-2 text-sm leading-6'>
                  <div className='flex items-start gap-2'>
                    <span className='text-green-600'>✓</span>
                    <span className='text-muted-foreground'>
                      Use filters to narrow down before exploring
                    </span>
                  </div>
                  <div className='flex items-start gap-2'>
                    <span className='text-green-600'>✓</span>
                    <span className='text-muted-foreground'>
                      Zoom to your specific area instead of viewing whole country
                    </span>
                  </div>
                  <div className='flex items-start gap-2'>
                    <span className='text-green-600'>✓</span>
                    <span className='text-muted-foreground'>
                      Close dealer panels you're not using
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader>
                <CardTitle className='text-lg font-semibold'>For Better Visualization</CardTitle>
              </CardHeader>
              <CardContent>
                <div className='space-y-2 text-sm leading-6'>
                  <div className='flex items-start gap-2'>
                    <span className='text-green-600'>✓</span>
                    <span className='text-muted-foreground'>
                      Zoom out to see big picture (clusters)
                    </span>
                  </div>
                  <div className='flex items-start gap-2'>
                    <span className='text-green-600'>✓</span>
                    <span className='text-muted-foreground'>
                      Zoom in for specific dealers (individual markers)
                    </span>
                  </div>
                  <div className='flex items-start gap-2'>
                    <span className='text-green-600'>✓</span>
                    <span className='text-muted-foreground'>
                      Use dark mode in low-light environments
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Mobile Usage Section */}
        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Mobile Usage</h2>
          <p className='text-sm text-muted-foreground mb-6 leading-6'>On phones and tablets:</p>
          <ul className='space-y-2 ml-6'>
            <li className='text-sm leading-6'>Use landscape orientation for best view</li>
            <li className='text-sm leading-6'>Pinch to zoom</li>
            <li className='text-sm leading-6'>Two-finger drag to pan</li>
            <li className='text-sm leading-6'>Tap markers for popups</li>
            <li className='text-sm leading-6'>Filter panel may stack on top of map</li>
            <li className='text-sm leading-6'>
              Collapse filters when not in use for more map space
            </li>
          </ul>
        </section>

        {/* Troubleshooting Section */}
        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-10'>Troubleshooting</h2>

          <div className='bg-amber-50 dark:bg-amber-950/30 border-l-4 border-amber-600 p-6 rounded-r-xl'>
            <div className='flex gap-4'>
              <AlertTriangle className='h-6 w-6 text-amber-600 flex-shrink-0 mt-1' />
              <div>
                <h4 className='text-lg font-semibold text-amber-900 dark:text-amber-100 mb-4'>
                  Common Issues & Solutions
                </h4>
                <div className='space-y-4'>
                  <div>
                    <h5 className='font-semibold mb-2 text-sm text-amber-900 dark:text-amber-100'>
                      Map Not Loading
                    </h5>
                    <ul className='space-y-1 ml-4 text-sm leading-6 text-amber-800 dark:text-amber-200'>
                      <li>Check internet connection</li>
                      <li>Refresh page (F5 or Ctrl+R / ⌘+R)</li>
                      <li>Clear browser cache</li>
                      <li>Try different browser</li>
                    </ul>
                  </div>
                  <div>
                    <h5 className='font-semibold mb-2 text-sm text-amber-900 dark:text-amber-100'>
                      No Markers Showing
                    </h5>
                    <ul className='space-y-1 ml-4 text-sm leading-6 text-amber-800 dark:text-amber-200'>
                      <li>Check if all dealers are excluded in Dealer Configuration</li>
                      <li>Clear all filters (may be too restrictive)</li>
                      <li>Zoom out to see if markers appear</li>
                    </ul>
                  </div>
                  <div>
                    <h5 className='font-semibold mb-2 text-sm text-amber-900 dark:text-amber-100'>
                      Map Performance Slow
                    </h5>
                    <ul className='space-y-1 ml-4 text-sm leading-6 text-amber-800 dark:text-amber-200'>
                      <li>Too many recalls visible (use filters)</li>
                      <li>Zoom in to reduce marker count</li>
                      <li>Close other browser tabs</li>
                      <li>Upgrade browser to latest version</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Next Steps Section */}
        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Next Steps</h2>
          <p className='text-sm text-muted-foreground mb-6 leading-6'>
            Now that you understand the Recall Map, explore these related features:
          </p>
          <ul className='space-y-2 ml-6'>
            <li className='text-sm leading-6'>
              <Link to='/docs/features/filters' className='text-primary hover:underline'>
                Filters System
              </Link>{' '}
              - Learn advanced filtering techniques
            </li>
            <li className='text-sm leading-6'>
              <Link to='/docs/features/manual-routing' className='text-primary hover:underline'>
                Manual Routing
              </Link>{' '}
              - Assign selected recalls to technicians
            </li>
            <li className='text-sm leading-6'>
              <Link to='/docs/features/recall-list' className='text-primary hover:underline'>
                Recall List & Search
              </Link>{' '}
              - View recalls in a table format
            </li>
          </ul>
        </section>
      </div>
    </DocsLayout>
  );
};
